import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User, UserCreateInput, UserUpdateInput, Role } from '@cms/models';
import { userApi } from '@/lib/api/user.api';
import { roleApi } from '@/lib/api/role.api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { CommonModal } from '@/components/common/CommonModal';

type UserFormData = {
  email: string;
  name: string;
  password: string;
  roleId: string;
  status: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

export type UserFormModalMode = 'create' | 'view' | 'edit';

interface UserFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: UserFormModalMode;
  /** null when mode is 'create' */
  readonly user: User | null;
  readonly onSuccess?: () => void;
}

export function UserFormModal({
  open,
  onClose,
  mode,
  user,
  onSuccess,
}: UserFormModalProps) {
  const queryClient = useQueryClient();
  const isCreate = mode === 'create';
  const isView = mode === 'view';

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => roleApi.getAll(),
  });
  const systemRoles = useMemo(() => roles.filter(role => role.isSystem === true), [roles]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      name: '',
      password: '',
      roleId: '',
      status: 'active',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        email: user.email ?? '',
        name: user.name ?? '',
        password: '',
        roleId: user.roleId ?? '',
        status: user.status ?? 'active',
      });
    } else if (isCreate) {
      reset({
        email: '',
        name: '',
        password: '',
        roleId: '',
        status: 'active',
      });
    }
  }, [user, isCreate, reset]);

  const createMutation = useMutation({
    mutationFn: (data: UserFormData) => {
      const payload: UserCreateInput = {
        email: data.email,
        name: data.name || undefined,
        password: data.password || undefined,
        roleId: data.roleId || undefined,
        isPlatformUser: true,
      };
      return userApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess?.();
      reset();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UserFormData) => {
      if (!user?.id) return Promise.reject(new Error('No user'));
      const payload: UserUpdateInput = {
        name: data.name || undefined,
        status: data.status || undefined,
        roleId: data.roleId || undefined,
      };
      return userApi.update(user.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess?.();
      onClose();
    },
  });
  const onSubmit = async (data: UserFormData) => {
    if (isView) return;
    try {
      if (isCreate) {
        await createMutation.mutateAsync(data);
      } else {
        await updateMutation.mutateAsync(data);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(
        err.response?.data?.message ??
          (isCreate ? 'Failed to create user' : 'Failed to update user')
      );
    }
  };

  let title = 'Edit System User';
  if (mode === 'create') title = 'Create System User';
  if (mode === 'view') title = 'System user details';

  const readOnly = isView;
  let submitLabel = 'Save';
  if (isCreate) {
    submitLabel = createMutation.isPending ? 'Creating…' : 'Create';
  } else if (updateMutation.isPending) {
    submitLabel = 'Saving…';
  }

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="user-form-modal-title"
      panelClassName="max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
      >
          <div>
            <label
              htmlFor="user-form-email"
              className="block text-sm font-medium text-foreground"
            >
              Email *
            </label>
            <input
              id="user-form-email"
              type="email"
              {...register('email', {
                required: isCreate ? 'Email is required' : false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              disabled={readOnly || !isCreate}
              readOnly={!isCreate}
              className={cn(
                inputClass,
                (readOnly || !isCreate) && 'cursor-default bg-muted/50'
              )}
            />
            {!isCreate && (
              <p className="mt-1 text-xs text-muted-foreground">
                Email cannot be changed
              </p>
            )}
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>

          {isCreate && (
            <div>
              <label
                htmlFor="user-form-password"
                className="block text-sm font-medium text-foreground"
              >
                Password (optional, min 8 characters)
              </label>
              <input
                id="user-form-password"
                type="password"
                {...register('password', {
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
                className={inputClass}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label
                htmlFor="user-form-name"
                className="block text-sm font-medium text-foreground"
              >
                Name
              </label>
              <input
                id="user-form-name"
                type="text"
                {...register('name')}
                disabled={readOnly}
                className={cn(
                  inputClass,
                  readOnly && 'cursor-default bg-muted/50'
                )}
              />
            </div>
            {readOnly ? (
              <div>
                <span className="block text-sm font-medium text-muted-foreground mb-1">
                  Status
                </span>
                <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                  {watch('status') || '—'}
                </span>
              </div>
            ) : (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Toggle
                    id="user-form-status"
                    label="Status"
                    checked={field.value === 'active'}
                    onCheckedChange={checked =>
                      field.onChange(checked ? 'active' : 'inactive')
                    }
                  />
                )}
              />
            )}
          </div>

          <div>
            <label
              htmlFor="user-form-roleId"
              className="block text-sm font-medium text-foreground"
            >
              Role
            </label>
            <select
              id="user-form-roleId"
              {...register('roleId')}
              disabled={readOnly}
              className={cn(
                inputClass,
                readOnly && 'cursor-default bg-muted/50'
              )}
            >
              <option value="">— No role —</option>
              {systemRoles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isCreate
                      ? createMutation.isPending
                      : updateMutation.isPending
                  }
                >
                  {submitLabel}
                </Button>
              </>
            )}
          </div>
      </form>
    </CommonModal>
  );
}
